import classNames from 'classnames';
import { observer } from 'mobx-react';
import { FC, Fragment, SyntheticEvent, useMemo } from 'react';

import {
    Banner,
    Button,
    ButtonGroup,
    Dropdown,
    DropdownProps,
    FileDescriptor,
    FilePicker,
    Form,
    Icon,
    Modal,
    Text,
} from '@servicetitan/design-system';

import { PlayGreetingStore } from '../stores/play-greeting.store';
import * as Styles from './play-greeting.module.less';

export const PlayGreeting: FC<{
    disabled?: boolean;
    displayLabel?: boolean;
    className?: string;
    onChange?: (value?: string) => void;
    store: PlayGreetingStore;
}> = observer(props => {
    const store = props.store;
    const { playGreeting } = store.formState.$;
    const { greetingType, url, file } = store.formState.$.customGreetingFormState.$;
    const { defaultGreetings, savedGreeting, newCustomGreeting } = store;
    const customGreeting = 'Custom Greeting';

    const getCustomOption = (key: string, value: string, text: string, isFile?: boolean) => {
        const content = isFile ? (
            <Fragment>
                <Icon el="span" className="c-neutral-90 m-r-1" name="file_upload" size="22px" />
                {text}
            </Fragment>
        ) : (
            text
        );
        return {
            key,
            value,
            text: content,
            content: (<Text>{content}</Text>) as JSX.Element | undefined,
        };
    };

    const options = useMemo(() => {
        const array = Object.keys(defaultGreetings).length
            ? [
                  ...Object.keys(defaultGreetings).map(key =>
                      getCustomOption(key, key, defaultGreetings[key])
                  ),
                  <Dropdown.Divider key="divider_0" disabled />,
              ]
            : [];
        if (savedGreeting && !defaultGreetings[savedGreeting.value]) {
            array.unshift(<Dropdown.Divider key="divider_1" disabled />);
            array.unshift(
                getCustomOption(
                    'savedCustom',
                    savedGreeting.value,
                    savedGreeting.text || savedGreeting.value,
                    savedGreeting.isFile
                )
            );
        }
        if (newCustomGreeting) {
            array.push(
                getCustomOption(
                    'selectedCustom',
                    newCustomGreeting.value,
                    newCustomGreeting.text,
                    newCustomGreeting.text !== newCustomGreeting.value
                )
            );
        }
        array.push({
            key: 'newCustom',
            value: customGreeting,
            text: customGreeting,
            content: undefined,
        });
        return array;
    }, [defaultGreetings, savedGreeting, newCustomGreeting]);

    const onSelectChange = (_0: SyntheticEvent<HTMLElement>, data: DropdownProps) => {
        if (data.value === customGreeting) {
            store.openModal();
        } else {
            playGreeting.onChange(data.value as string);
        }
    };
    const onToggleboxClick = (value: string, checked: boolean) => {
        if (!checked && greetingType.value !== value) {
            greetingType.onChange(value);
        }
    };
    const onFileSelected = (files: FileList) => {
        store.uploadFile(files[0]);
    };
    const onFileReplace = (event: { file: FileDescriptor; newFile: File }) => {
        store.uploadFile(event.newFile);
    };
    const onFileDelete = () => {
        file.onChange(undefined);
    };

    if (props.onChange) {
        playGreeting.onDidChange(({ newValue }) => {
            props.onChange!(newValue);
        });
    }

    return (
        <Fragment>
            <Form.Select
                className={classNames(Styles.greetingSelect, 'm-b-0-i m-r-0-i', props.className)}
                large
                compact
                clearable
                options={options}
                onChange={onSelectChange}
                value={playGreeting.value}
                label={
                    props.displayLabel && (
                        <Text el="span" size={2} bold>
                            Play Greeting
                        </Text>
                    )
                }
                disabled={props.disabled}
                selectOnNavigation={false}
                selectOnBlur={false}
            />
            {!props.disabled && store.isModalOpen && (
                <Modal
                    open
                    closable
                    onClose={store.closeModal}
                    size={Modal.Sizes.S}
                    title="Set Custom Greeting"
                    footer={
                        <ButtonGroup>
                            <Button onClick={store.closeModal}>Cancel</Button>
                            <Button
                                primary
                                disabled={
                                    !greetingType.value ||
                                    (greetingType.value === 'File' && !file.value) ||
                                    (greetingType.value === 'Url' && !url.value)
                                }
                                onClick={store.uploadCustomGreeting}
                            >
                                Upload
                            </Button>
                        </ButtonGroup>
                    }
                >
                    <Form>
                        <Text className="m-t-0-i m-b-3-i">
                            Select a customized greeting for your customers to hear.
                        </Text>
                        <Form.Togglebox
                            className="m-b-2-i"
                            control="radio"
                            label={
                                <Fragment>
                                    <Text className="m-b-1-i" size={2} bold>
                                        Upload a File
                                    </Text>
                                    <FilePicker
                                        className={classNames(
                                            Styles.greetingFileSelect,
                                            file.value && greetingType.value === 'Url'
                                                ? Styles.inactive
                                                : ''
                                        )}
                                        multiple={false}
                                        value={file.value}
                                        onSelected={onFileSelected}
                                        onReplace={onFileReplace}
                                        onDelete={onFileDelete}
                                        accept=".wav, .mp3"
                                    />
                                </Fragment>
                            }
                            value="File"
                            name="CustomGreeting"
                            onClick={onToggleboxClick}
                            checked={greetingType.value === 'File'}
                        />
                        <Form.Togglebox
                            className="m-b-0-i"
                            control="radio"
                            label={
                                <Fragment>
                                    <Text className="m-b-1-i" size={2} bold>
                                        Or Import from URL
                                    </Text>
                                    <Form.Input
                                        placeholder="Please input recording URL"
                                        value={url.value}
                                        onChange={url.onChangeHandler}
                                        disabled={greetingType.value === 'File'}
                                        error={greetingType.value === 'Url' && url.hasError}
                                    />
                                </Fragment>
                            }
                            value="Url"
                            name="CustomGreeting"
                            onClick={onToggleboxClick}
                            checked={greetingType.value === 'Url'}
                            error={greetingType.value === 'Url' && url.hasError}
                        />
                        {greetingType.value === 'Url' && url.hasError && (
                            <Banner
                                className="m-t-2"
                                status="critical"
                                icon
                                title={
                                    url.error || 'Please input a valid URL for .mp3 or .wav file'
                                }
                            />
                        )}
                    </Form>
                </Modal>
            )}
        </Fragment>
    );
});
